import { DatePipe, DecimalPipe } from '@angular/common'
import { Component, EventEmitter, Output } from '@angular/core'

import { BaseComponent, FeatureType } from '../../shared'

export interface JobDueForVisitRow {
    jobId: string
    jobName: string
    siteSuperName: string
    isDue: boolean
    dueReasons: string[]
    lastVisitDate: string | null
    lastVisitId: string | null
    lastVisitedByName: string
    daysSinceLastVisit: number | null
    hoursSinceLastVisit: number
    currentLaborHours: number
    hoursUntilDue: number
    dueByDate: string | null
}

interface JobsDueForVisitResponse {
    totalDueCount: number
    jobs: JobDueForVisitRow[]
}

interface LeadershipTeamVisitDashboardMetrics {
    jobsDueCount: number
    jobsNeverVisitedCount: number
    visitsLast30Days: number
    visitsLast90Days: number
    averageDaysBetweenVisits: number | null
    visitsByMemberLast90Days: { memberName: string; visitCount: number }[]
}

const emptyMetrics = (): LeadershipTeamVisitDashboardMetrics => ({
    jobsDueCount: 0,
    jobsNeverVisitedCount: 0,
    visitsLast30Days: 0,
    visitsLast90Days: 0,
    averageDaysBetweenVisits: null,
    visitsByMemberLast90Days: [],
})

@Component({
    selector: 'app-leadership-team-visits-dashboard',
    templateUrl: './leadership-team-visits-dashboard.component.html',
    styleUrls: ['./leadership-team-visits-dashboard.component.css'],
    standalone: true,
    imports: [DatePipe, DecimalPipe],
})
export class LeadershipTeamVisitsDashboardComponent extends BaseComponent {
    @Output() addForJob = new EventEmitter<{ jobId: string; jobName: string }>()

    jobsDueForVisit: JobDueForVisitRow[] = []
    dueCount = 0
    dueQueueLoadError = false
    metricsLoadError = false
    metrics: LeadershipTeamVisitDashboardMetrics = emptyMetrics()

    constructor() {
        super()
        this.refresh()
    }

    refresh() {
        this.cancelableRequest(
            'ltvDashboard',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get<JobsDueForVisitResponse>('/api/LeadershipTeamVisit/GetJobsDueForVisit'),
                    this.services.data.get<LeadershipTeamVisitDashboardMetrics>('/api/LeadershipTeamVisit/GetDashboardMetrics'),
                )
                .subscribe({
                    next: results => {
                        const dueResponse = results[0]
                        const metricsResponse = results[1]

                        this.jobsDueForVisit = dueResponse?.jobs ?? []
                        this.dueCount = dueResponse?.totalDueCount ?? this.jobsDueForVisit.length
                        this.dueQueueLoadError = false

                        this.metrics = metricsResponse ?? emptyMetrics()
                        this.metricsLoadError = false
                        this.initialLoadingComplete()
                    },
                    error: () => {
                        this.jobsDueForVisit = []
                        this.dueCount = 0
                        this.dueQueueLoadError = true
                        this.metrics = emptyMetrics()
                        this.metricsLoadError = true
                        this.initialLoadingComplete()
                    },
                }),
        )
    }

    formatJobDisplay(jobId: string, jobName: string): string {
        if (!jobName || jobName === jobId) {
            return jobId
        }
        return `${jobId} — ${jobName}`
    }

    formatDueReasons(reasons: string[]): string {
        return (reasons ?? [])
            .map(reason => {
                switch (reason) {
                    case 'NoPriorEvent':
                        return 'No leadership visit on file'
                    case 'TimeThreshold':
                        return '1+ calendar month since last visit'
                    case 'HoursThreshold':
                        return '1,200+ labor hours since last visit'
                    default:
                        return reason
                }
            })
            .join('; ')
    }

    isUrgentDueRow(row: JobDueForVisitRow): boolean {
        return row.dueReasons.includes('NoPriorEvent') || row.dueReasons.length > 1
    }

    formatHours(value: number): string {
        return Math.round(value ?? 0).toLocaleString()
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.OperationsLeadershipTeamVisitsEdit])
    }

    maxVisitCount(): number {
        return Math.max(...this.metrics.visitsByMemberLast90Days.map(m => m.visitCount), 1)
    }
}
